import React from 'react';
import { EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import config from 'config';
import { LogoMark } from 'components/global/LogoMark';
import { StatusBadge } from 'components/ui/status-badge';
import { RenderedHtml, htmlIsEmpty } from 'modules/editor';
import { formatDateLong } from 'lib/dateFormat';
import type * as Types from '../types';

type Result = Types.IApi.GuestResult.Response;

interface TestResultSummaryProps {
  data: Result;
  /** Result actions (copy link, PDF): in the brand row on desktop, stacked under the card on phones. */
  actions?: React.ReactNode;
}

const DEFAULT_FLAGS: Types.IApi.GuestResult.VisibilityFlags = {
  showPercentage: true,
  showPoints: true,
  showPassFailMessage: true,
  showCorrectAnswers: false,
};

const pad = (n: number): string => String(n).padStart(2, '0');

function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
}

function formatClock(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-[13px] text-muted-foreground">{children}</span>
);

/**
 * One card: the test, the participant, the score as a plain 52px figure with
 * its pass/fail badge, the time spent, then the summary. Rendered by the public
 * result page (real data) and the author Preview (sample), so preview === output.
 */
const TestResultSummary: React.FC<TestResultSummaryProps> = ({ data, actions }) => {
  const { t } = useTranslation();
  const flags = data.flags ?? DEFAULT_FLAGS;
  const passed = data.passed ?? null;
  const percentage = Math.max(0, Math.min(100, Math.round(data.percentage ?? 0)));
  const points = flags.showPoints && data.maxScore != null ? `${data.score ?? 0} / ${data.maxScore}` : null;

  const showEndMessage = !htmlIsEmpty(data.endMessage ?? '');
  const showOutcomeMessage = flags.showPassFailMessage && !htmlIsEmpty(data.message ?? '');
  const hasSummary = showEndMessage || showOutcomeMessage;

  const duration = data.durationMs != null ? formatDuration(data.durationMs) : null;
  const timeRows: Array<[string, string]> = [];
  const startClock = formatClock(data.startedAt);
  const endClock = formatClock(data.submittedAt);
  const dateSource = data.submittedAt ?? data.startedAt;
  if (startClock) timeRows.push([t('result_start_time'), startClock]);
  if (endClock) timeRows.push([t('result_end_time'), endClock]);
  if (dateSource) timeRows.push([t('result_date'), formatDateLong(dateSource)]);
  const hasTiming = Boolean(duration || timeRows.length);

  // The big figure is the percentage; without it, the points take its place.
  const figure = flags.showPercentage ? `${percentage}%` : points;

  return (
    <div className="mx-auto grid w-full max-w-[760px] gap-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
      <a
        href={config.landing_url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex w-fit items-center gap-2.5 rounded-md transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:col-start-1 sm:row-start-1"
        aria-label="Examing"
      >
        <LogoMark size={26} className="rounded-[7px]" />
        <span className="font-display text-sm font-extrabold tracking-[-0.02em] text-foreground/80">Examing</span>
      </a>

      {actions && (
        <div className="order-last flex flex-col gap-2.5 sm:order-none sm:col-start-2 sm:row-start-1 sm:flex-row">
          {actions}
        </div>
      )}

      <article className="rounded-xl border border-border bg-card px-4 py-5 sm:col-span-2 sm:row-start-2 sm:px-8 sm:py-7">
        <Label>{t('result_result')}</Label>
        <h1 className="mt-1.5 text-[22px] font-bold leading-[1.25] tracking-[-0.02em] text-foreground sm:text-[26px]">
          {data.testTitle}
        </h1>

        <div className="mt-5 grid gap-x-7 gap-y-[18px] border-t border-border pt-5 sm:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
          <div className="flex flex-col gap-1.5">
            {data.fullName && (
              <>
                <Label>{t('result_respondent')}</Label>
                <span className="text-[17px] font-semibold text-foreground">{data.fullName}</span>
              </>
            )}
            <div className="mt-3.5 flex items-end gap-4">
              {figure && (
                <span className="text-[52px] font-bold leading-[0.95] tracking-[-0.04em] tabular-nums text-foreground">
                  {figure}
                </span>
              )}
              <span className="flex flex-col gap-1.5 pb-0.5">
                {passed != null ? (
                  <StatusBadge tone={passed ? 'success' : 'error'} className="self-start">
                    {passed ? t('result_test_passed') : t('result_test_failed')}
                  </StatusBadge>
                ) : (
                  <span className="text-sm font-medium text-foreground">{t('result_available')}</span>
                )}
                {points && flags.showPercentage && (
                  <span className="text-[13.5px] text-muted-foreground">
                    <span className="font-semibold tabular-nums text-foreground">{points}</span> {t('result_points_word')}
                  </span>
                )}
              </span>
            </div>
          </div>

          {hasTiming && (
            <dl className="grid grid-cols-[auto_1fr] content-start gap-x-4 border-t border-border pt-[18px] text-sm sm:border-l sm:border-t-0 sm:pl-7 sm:pt-0">
              <dt className="col-span-2">
                <Label>{t('result_timer')}</Label>
              </dt>
              <dd className="col-span-2 mb-3 mt-1 text-2xl font-bold tabular-nums text-foreground">{duration ?? '—'}</dd>
              {timeRows.map(([label, value]) => (
                <React.Fragment key={label}>
                  <dt className="border-t border-border py-2 text-muted-foreground">{label}</dt>
                  <dd className="border-t border-border py-2 text-right tabular-nums text-foreground">{value}</dd>
                </React.Fragment>
              ))}
            </dl>
          )}
        </div>

        {hasSummary && (
          <div className="mt-5 flex flex-col gap-1.5 border-t border-border pt-[18px]">
            <Label>{t('result_summary')}</Label>
            <div className="space-y-2 text-[15px] leading-relaxed text-foreground">
              {showEndMessage && <RenderedHtml html={data.endMessage ?? ''} />}
              {showOutcomeMessage && <RenderedHtml html={data.message ?? ''} />}
            </div>
          </div>
        )}

        {!flags.showCorrectAnswers && (
          <div className="mt-5 flex items-center gap-2.5 border-t border-border pt-[18px] text-sm text-muted-foreground">
            <EyeOff className="h-4 w-4 shrink-0 text-muted-foreground/70" aria-hidden />
            {t('result_correct_answers_hidden')}
          </div>
        )}
      </article>
    </div>
  );
};

export default TestResultSummary;
